import { isValidSuiAddress } from '@mysten/sui/utils';
import { bech32 } from 'bech32';
import bs58 from 'bs58';
import { isAddress as isValidEVMAddress } from 'viem';

import type { ChainRuntime, NearIntentsAddress } from '@infinex/chain-config';
import {
  DEFUSE_ASSET_ID_TO_CHAIN_ADDRESS_ID,
  NEAR_INTENTS_BITCOIN_LEGACY_ADDRESS,
  NEAR_INTENTS_DOGECOIN_ADDRESS,
  NEAR_INTENTS_XRP_ADDRESS,
  NEAR_INTENTS_ZEC_ADDRESS,
  NEAR_INTENTS_ADA_ADDRESS,
  NEAR_INTENTS_LTC_ADDRESS,
  networks,
} from '@infinex/chain-config';

import { fromBase58Check, fromBech32 } from './addressUtils';

export type BitcoinAddress = `bc1${string}` | `1${string}` | `3${string}`;

export type DogecoinAddress =
  | `D${string}`
  | `A${string}`
  | `9${string}`
  | `doge${string}`;

export type CardanoAddress = `addr1${string}`;

export type XrpAddress = XrpLegacyAddress | XrpXAddress;
export type XrpLegacyAddress = `r${string}`;
export type XrpXAddress = `X${string}`;

export type ZecTransparentAddress = `t1${string}` | `t3${string}`;
export type ZecShieldedAddress = `zs${string}` | `ztestsapling${string}`;
export type ZecUnifiedAddress = `u1${string}`;
export type ZecSproutAddress = `zc${string}`;

export type ZecAddress =
  | ZecTransparentAddress
  | ZecShieldedAddress
  | ZecUnifiedAddress
  | ZecSproutAddress;

export type LitecoinBase58MainnetAddress =
  | `L${string}`
  | `M${string}`
  | `3${string}`;
export type LitecoinBase58TestnetAddress =
  | `m${string}`
  | `n${string}`
  | `Q${string}`
  | `2${string}`;
export type LitecoinBase58Address =
  | LitecoinBase58MainnetAddress
  | LitecoinBase58TestnetAddress;

export type LitecoinBech32Address = `ltc1${string}` | `tltc1${string}`;

export type LitecoinAddress = LitecoinBase58Address | LitecoinBech32Address;

/**
 * NEAR Intents token ids: `nep141:<contract>` for single-token contracts, or
 * `nep245:<contract>:<token_id>` for multi-token bridge contracts (e.g. the HOT
 * omni bridge, where one contract carries many tokens). A NEP-245 `token_id` is
 * an arbitrary string, so everything after the contract separator is accepted.
 */
const NEAR_INTENTS_TOKEN_ID_PATTERN = /^(?:nep141:[^\s:]+|nep245:[^\s:]+:\S+)$/;

export function isNearIntentsTokenId(address: string): boolean {
  return NEAR_INTENTS_TOKEN_ID_PATTERN.test(address);
}

export function getNearIntentsAddress(address: string): NearIntentsAddress {
  if (!isNearIntentsTokenId(address)) {
    throw new Error('Invalid NEAR Intents address');
  }

  return address as NearIntentsAddress;
}

export function isNearIntentsOfficialAssetAddress(
  address: string
): address is NearIntentsAddress {
  return address in DEFUSE_ASSET_ID_TO_CHAIN_ADDRESS_ID;
}

/** A branded type representing a validated NEAR account */
export type NearAccount = string & { __brand: 'NearAccount' };

/**
 * Validates a NEAR account ID according to nomicon's slop.
 * NEAR has such loose validation rules i know
 *
 * @see https://nomicon.io/DataStructures/Account
 */
export function validateNearAccountAddress(accountId: string): {
  isValid: boolean;
  accountId?: NearAccount;
} {
  // Reserved system account cannot be used
  if (accountId === 'system') {
    return { isValid: false };
  }

  // Check length constraints (min 2, max 64)
  if (accountId.length < 2 || accountId.length > 64) {
    return { isValid: false };
  }

  // NEAR-implicit account: 64 lowercase hex characters (Ed25519 public key)
  if (accountId.length === 64 && /^[0-9a-f]+$/.test(accountId)) {
    return { isValid: true, accountId: accountId as NearAccount };
  }

  // ETH-implicit account: 0x followed by 40 lowercase hex characters
  if (/^0x[0-9a-f]{40}$/.test(accountId)) {
    return { isValid: true, accountId: accountId as NearAccount };
  }

  // NEAR-deterministic account: 0s followed by 40 lowercase hex characters
  if (/^0s[0-9a-f]{40}$/.test(accountId)) {
    return { isValid: true, accountId: accountId as NearAccount };
  }

  // Standard named account regex from NEAR spec (length already checked above)
  // ^(([a-z\d]+[\-_])*[a-z\d]+\.)*([a-z\d]+[\-_])*[a-z\d]+$
  //
  // This regex ensures:
  // - Parts are separated by `.`
  // - Each part starts and ends with alphanumeric
  // - `-` or `_` can only appear between alphanumeric characters
  // - No consecutive separators, no leading/trailing separators
  const nearNamedAccountRegex =
    /^(([a-z\d]+[-_])*[a-z\d]+\.)*([a-z\d]+[-_])*[a-z\d]+$/;

  if (!nearNamedAccountRegex.test(accountId)) {
    return { isValid: false };
  }

  return { isValid: true, accountId: accountId as NearAccount };
}

export function isValidNearAddress(address: string): boolean {
  return validateNearAccountAddress(address).isValid;
}

export function isValidNearIntentsAddress(
  address: string
): address is
  | BitcoinAddress
  | DogecoinAddress
  | XrpAddress
  | ZecAddress
  | CardanoAddress {
  return (
    isValidBitcoinAddress(address) ||
    isValidDogecoinAddress(address) ||
    isValidXrpAddress(address) ||
    isValidZecAddress(address) ||
    isValidCardanoAddress(address) ||
    isValidLitecoinAddress(address)
  );
}

/**
 * Given a withdrawal address (e.g. a BTC address), returns the nearIntents
 * token address (e.g. `nep141:btc.omft.near`) that the address corresponds to.
 * Returns null if the address is not a valid nearIntents withdrawal address.
 */
export function detectNearIntentsTokenAddress(
  address: string
): NearIntentsAddress | null {
  // A BTC address maps to `btc.omft.near`: in the 1Click/defuse registry that
  // token remains the btc-blockchain variant, whereas `nbtc.bridge.near` is
  // registered as a NEAR-chain token — so flows that deliver to an external
  // BTC address (which is what this detection feeds) must use it.
  if (isValidBitcoinAddress(address))
    return NEAR_INTENTS_BITCOIN_LEGACY_ADDRESS;
  if (isValidDogecoinAddress(address)) return NEAR_INTENTS_DOGECOIN_ADDRESS;
  if (isValidXrpAddress(address)) return NEAR_INTENTS_XRP_ADDRESS;
  if (isValidZecAddress(address)) return NEAR_INTENTS_ZEC_ADDRESS;
  if (isValidCardanoAddress(address)) return NEAR_INTENTS_ADA_ADDRESS;
  if (isValidLitecoinAddress(address)) return NEAR_INTENTS_LTC_ADDRESS;
  return null;
}

/**
 * Validates a Bitcoin address for both mainnet and testnet.
 *
 * @param {string} address - The Bitcoin address to validate.
 * @returns {address is BitcoinAddress} - Returns validity
 */
export function isValidBitcoinAddress(
  address: string
): address is BitcoinAddress {
  try {
    // Attempt to parse as Base58Check (legacy and P2SH)
    const { version } = fromBase58Check(address);

    if (
      version === networks.bitcoin.mainnet.pubKeyHash ||
      version === networks.bitcoin.mainnet.scriptHash
    ) {
      // Valid Mainnet Address
      return true;
    }

    if (
      version === networks.bitcoin.testnet.pubKeyHash ||
      version === networks.bitcoin.testnet.scriptHash
    ) {
      // Valid Testnet Address
      return true;
    }

    // If version doesn't match known types
    return false;
  } catch (unusedError) {
    try {
      // Attempt to parse as Bech32 (SegWit)
      const decoded = fromBech32(address);

      if (decoded.prefix === 'bc') {
        // Valid Mainnet Bech32 Address
        return true;
      }

      if (decoded.prefix === 'tb') {
        // Valid Testnet Bech32 Address
        return true;
      }

      // If prefix doesn't match known types
      return false;
    } catch (unusedError) {
      // If both parsing attempts fail, address is invalid
      return false;
    }
  }
}

/**
 * Validates a Dogecoin address for both mainnet and testnet.
 *
 * @param {string} address - The Dogecoin address to validate.
 * @returns {address is DogecoinAddress} - Returns validity
 */
export function isValidDogecoinAddress(
  address: string
): address is DogecoinAddress {
  try {
    // Attempt to decode the address as Base58Check (legacy P2PKH or P2SH)
    const { version } = fromBase58Check(address);

    // Check Dogecoin mainnet versions
    if (
      version === networks.dogecoin.mainnet.pubKeyHash ||
      version === networks.dogecoin.mainnet.scriptHash
    ) {
      return true;
    }

    // Check Dogecoin testnet versions
    if (
      version === networks.dogecoin.testnet.pubKeyHash ||
      version === networks.dogecoin.testnet.scriptHash
    ) {
      return true;
    }

    // If version doesn't match known Dogecoin types
    return false;
  } catch (unusedError) {
    // If parsing fails, address is invalid
    return false;
  }
}

// XRP Ledger's Base58 alphabet (note the omission of 0, O, I, and l)
const XRP_BASE58_ALPHABET =
  'rpshnaf39wBUDNEGHJKLM4PQRST7VWXYZ2bcdeCg65jkm8oFqi1tuvAxyz';

// TODO: @MIN RE-IMPLEMENT XRP ADDRESS VALIDATION AT A LATER DATE: https://github.com/infinex-xyz/platform/pull/7310

/**
 * Custom Base58 decoder for XRP addresses using the base-x library.
 * XRP uses a different alphabet than Bitcoin's standard Base58.
 */
// const xrpBase58 = baseX(XRP_BASE58_ALPHABET);

/**
 * Validates an XRP Ledger classic address.
 *
 * This function checks that:
 * - Delegates X-Address checking to `validateXrpXAddress`.
 * - The address length is between 25 and 35 characters.
 * - It starts with "r" (indicating a classic address; X-addresses start with X or T).
 * - It only uses valid characters from the XRP Base58 alphabet.
 * - When decoded (using base58) it yields 25 bytes:
 *    - 1 byte: version prefix (should be 0x00)
 *    - 20 bytes: AccountID
 *    - 4 bytes: checksum (which must equal the first 4 bytes of the double SHA‑256 of the payload)
 *
 * @param address - The XRP Ledger address to validate.
 * @returns {address is XrpAddress} - Returns validity
 */
export function isValidXrpAddress(address: string): address is XrpAddress {
  // Validate X-addresses seperately
  if (address[0] === 'X' || address[0] === 'T') {
    return isValidXrpXAddress(address);
  }

  // Remove any tags from the address
  const cleanedAddress = address.split(':')[0];

  // Basic length and starting character checks
  if (cleanedAddress.length < 25 || cleanedAddress.length > 35) return false;
  if (cleanedAddress[0] !== 'r') return false;

  // Check that every character is in the XRP Base58 alphabet.
  for (const char of cleanedAddress) {
    if (!XRP_BASE58_ALPHABET.includes(char)) return false;
  }

  return true;
}

/**
 * Validates an XRP Ledger X address.
 *
 * This function checks that:
 * - The address length is 47 characters.
 * - It starts with "X" or "T" (indicating a X-address; classic addresses start with "r").
 * - It only uses valid characters from the XRP Base58 alphabet.
 * - When decoded (using base58) it yields 25 bytes:
 *    - 2 bytes: prefix (0x05 0x44 for mainnet, 0x04 0x93 for testnet)
 *    - 20 bytes: account ID (classic address)
 *    - 0 or 4 bytes: destination tag (if present)
 *    - 1 byte: flag/reserved (usually 0x00 or 0x01 to indicate presence of tag)
 *    - 4 bytes: checksum (Base58Check: SHA256(SHA256(payload)) first 4 bytes)
 *
 * @param address - The XRP Ledger address to validate.
 * @returns {address is XrpXAddress} - Returns validity
 */
function isValidXrpXAddress(address: string): address is XrpXAddress {
  return (
    address.length === 47 &&
    (address.startsWith('X') || address.startsWith('T')) &&
    [...address].every((char) => XRP_BASE58_ALPHABET.includes(char))
  );
}

/**
 * Validates a ZEC mainnet address.
 *
 * @param {string} address - The ZEC address to validate.
 * @returns {address is ZecAddress} - Returns validity
 */
export function isValidZecAddress(address: string): address is ZecAddress {
  try {
    const mainnet = networks.zcash.mainnet;

    // Transparent t-address (Base58Check)
    if (address.startsWith('t')) {
      const { version } = fromBase58Check(address);
      return version === mainnet.pubKeyHash || version === mainnet.scriptHash;
    }

    // Shielded, Unified and Sprout all use Bech32
    const { prefix, encoding } = fromBech32(address, 'zcash');

    // Shielded Sapling (Bech32)
    if (address.startsWith(mainnet.saplingHrp)) {
      return prefix === mainnet.saplingHrp && encoding === 'bech32';
    }

    // Unified Address (Bech32m)
    if (address.startsWith(mainnet.unifiedHrp)) {
      return prefix === mainnet.unifiedHrp && encoding === 'bech32m';
    }

    // Sprout legacy (Bech32)
    if (address.startsWith(mainnet.sproutHrp)) {
      return prefix === mainnet.sproutHrp && encoding === 'bech32';
    }

    return false;
  } catch {
    return false;
  }
}

// NEAR Intents currently only supports Base and Enterprise Shelley addresses, so we only check those
export function isValidCardanoAddress(
  address: string
): address is CardanoAddress {
  try {
    const { prefix, words } = bech32.decode(address, 2048);
    if (prefix !== 'addr') return false; // mainnet only

    const bytes = Uint8Array.from(bech32.fromWords(words));
    if (bytes.length < 2) return false;

    const header = bytes[0];
    const type = header >> 4; // high nibble
    const net = header & 0x0f; // low nibble

    if (net !== 1) return false; // must be mainnet

    // base addresses: high nibble = 0..3, length = 57 bytes
    if (type >= 0 && type <= 3) return bytes.length === 57;

    // enterprise addresses: high nibble = 6 or 7, length = 29 bytes
    if (type === 6 || type === 7) return bytes.length === 29;

    return false;
  } catch {
    return false;
  }
}

/**
 * Validates a Litecoin address for both mainnet and testnet.
 *
 * Accepts:
 * - Base58Check: L... (P2PKH), M... (P2SH new), 3... (P2SH legacy), Q... (testnet P2SH new), 2... (testnet P2SH legacy)
 * - SegWit: ltc1... / tltc1... (both bech32 v0 and bech32m v1)
 */
export function isValidLitecoinAddress(
  address: string
): address is LitecoinAddress {
  // 1) Try Base58Check (P2PKH/P2SH)
  try {
    const { version } = fromBase58Check(address);

    // Mainnet Base58
    if (
      version === networks.litecoin.mainnet.pubKeyHash || // 0x30
      version === networks.litecoin.mainnet.scriptHash || // 0x32 (new M...)
      version === networks.litecoin.mainnet.scriptHashLegacy // 0x05 (legacy 3...)
    ) {
      return true;
    }

    // Testnet Base58
    if (
      version === networks.litecoin.testnet.pubKeyHash || // 0x6f
      version === networks.litecoin.testnet.scriptHash || // 0x3a (new Q...)
      version === networks.litecoin.testnet.scriptHashLegacy // 0xc4 (legacy 2...)
    ) {
      return true;
    }

    return false;
  } catch (_unusedBase58Err) {
    try {
      // Try Bech32 / Bech32m
      const decoded = fromBech32(address);

      if (
        decoded.prefix === networks.litecoin.mainnet.hrp || // 'ltc'
        decoded.prefix === networks.litecoin.testnet.hrp // 'tltc'
      ) {
        return true;
      }

      return false;
    } catch (_unusedBech32Error) {
      return false;
    }
  }
}

export function detectChainRuntimeFromAddress(
  address: string | null | undefined
): ChainRuntime | null {
  // TODO: @BEN Figure out why this is necessary...
  if (!address) {
    return null;
  }

  try {
    // Check for ENS names first
    if (address.endsWith('.eth') || isValidEVMAddress(address)) {
      return 'evm';
    }

    if (isValidNearIntentsAddress(address)) {
      return 'nearIntents';
    }

    // Check for NEAR implicit account (64 hex chars) or named account
    if (isValidNearAddress(address)) {
      return 'near';
    }

    // Check if it's a valid Sui address using the official validator
    if (isValidSuiAddress(address)) {
      return 'sui';
    }

    // Quick Solana check - base58 decode and length 32
    const decoded = bs58.decode(address);
    if (decoded.length === 32) {
      return 'svm';
    }

    return null;
  } catch (unusedError) {
    return null;
  }
}

export { isValidEVMAddress, isValidSuiAddress };
